'use client';

import { c as _c } from "react/compiler-runtime";
import type { ProjectConfiguration } from '@/api/generated/model';
import { PARAMS_KEY_PROJECT_ID } from '@/constants/route-params-keys';
import { getEnabledNebulyPages } from '@/domains/pages/registry';
import type { NebulyPageDefinition, NebulyPageKey } from '@/domains/pages/types';
import { useReportSubproductQueryValue } from '@/domains/reports/hooks/use-report-subproduct';
import type { ReportSubproduct } from '@/domains/reports/utils/report-subproduct.utils';
import { useConfig } from '@/hooks/use-config';
import type { ProjectModel } from '@/models/organizations/internal-models';
import { useProjectsContext } from '@/providers/projects-provider';
import { useQueryParamStateV2 } from '@/utils/use-query-param-state';
import { useMemo } from 'react';
const LIBRARY_PAGE_SUBPRODUCTS: ReportSubproduct[] = ['roi', 'aifi'];
export function getAllEnabledNebulyPages(config: Pick<ProjectConfiguration, 'roi_settings' | 'aifi_settings'> | undefined, product?: ProjectModel['product']): NebulyPageDefinition[] {
  const byKey = new Map<NebulyPageKey, NebulyPageDefinition>();
  for (const subproduct of LIBRARY_PAGE_SUBPRODUCTS) {
    if (subproduct === 'roi' && product !== 'internal') continue;
    for (const page of getEnabledNebulyPages(subproduct, config)) {
      byKey.set(page.key, page);
    }
  }
  return [...byKey.values()];
}
export function useAllEnabledNebulyPages() {
  const $ = _c(7);
  const {
    selectedProject,
    projects
  } = useProjectsContext();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      type: "string"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [urlProjectId] = useQueryParamStateV2(PARAMS_KEY_PROJECT_ID, null, t0);
  const configProjectId = selectedProject?.mappedProjectId ?? selectedProject?.id ?? urlProjectId ?? undefined;
  const {
    data: config
  } = useConfig(configProjectId);
  let t1;
  bb0: {
    if (selectedProject && (selectedProject.id === configProjectId || selectedProject.mappedProjectId === configProjectId)) {
      t1 = selectedProject.product;
      break bb0;
    }
    let t2;
    if ($[1] !== configProjectId || $[2] !== projects) {
      t2 = projects.find(project => project.id === configProjectId)?.product;
      $[1] = configProjectId;
      $[2] = projects;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    t1 = t2;
  }
  const product = t1;
  let t2;
  if ($[4] !== config || $[5] !== product) {
    t2 = getAllEnabledNebulyPages(config, product);
    $[4] = config;
    $[5] = product;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
export function useEnabledNebulyPages() {
  const $ = _c(5);
  const subproduct = useReportSubproductQueryValue();
  const {
    selectedProject
  } = useProjectsContext();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      type: "string"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [urlProjectId] = useQueryParamStateV2(PARAMS_KEY_PROJECT_ID, null, t0);
  const configProjectId = selectedProject?.mappedProjectId ?? selectedProject?.id ?? urlProjectId ?? undefined;
  const {
    data: config
  } = useConfig(configProjectId);
  let t1;
  bb0: {
    if (!subproduct) {
      let t2;
      if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[1] = t2;
      } else {
        t2 = $[1];
      }
      t1 = t2;
      break bb0;
    }
    let t2;
    if ($[2] !== config || $[3] !== subproduct) {
      t2 = getEnabledNebulyPages(subproduct, config);
      $[2] = config;
      $[3] = subproduct;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = t2;
  }
  return t1;
}
export function useIsSubproductSelected(subproduct) {
  return useReportSubproductQueryValue() === subproduct;
}
export function useNebulyPagesForSubproduct(subproduct) {
  const $ = _c(5);
  const {
    selectedProject
  } = useProjectsContext();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      type: "string"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [urlProjectId] = useQueryParamStateV2(PARAMS_KEY_PROJECT_ID, null, t0);
  const configProjectId = selectedProject?.mappedProjectId ?? selectedProject?.id ?? urlProjectId ?? undefined;
  const {
    data: config
  } = useConfig(configProjectId);
  let t1;
  bb0: {
    if (!subproduct) {
      let t2;
      if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[1] = t2;
      } else {
        t2 = $[1];
      }
      t1 = t2;
      break bb0;
    }
    let t2;
    if ($[2] !== config || $[3] !== subproduct) {
      t2 = getEnabledNebulyPages(subproduct, config);
      $[2] = config;
      $[3] = subproduct;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = t2;
  }
  return t1;
}

/** Enabled aifi pages — for Proficiency left-rail landing and workspace layout seeding. */
export function useAifiNebulyPages() {
  return useNebulyPagesForSubproduct("aifi");
}
export function useIsAifiSubproductSelected() {
  return useIsSubproductSelected("aifi");
}
export function useRoiNebulyPages() {
  return useNebulyPagesForSubproduct("roi");
}
export function useIsRoiSubproductSelected() {
  return useIsSubproductSelected("roi");
}